import { Injectable, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import { QUESTIONNAIRE_TEMPLATES_SEED } from '@common/data';
import type { QuestionnaireTemplate } from '@common/types';

const LIST_LATENCY_MS = 300;
const SAVE_LATENCY_MS = 400;

@Injectable({ providedIn: 'root' })
export class QuestionnaireTemplatesService {
  private readonly templates = signal<ReadonlyArray<QuestionnaireTemplate>>(
    QUESTIONNAIRE_TEMPLATES_SEED,
  );

  readonly all = this.templates.asReadonly();

  forOrganization(orgId: string): Observable<QuestionnaireTemplate[]> {
    return of(this.templates().filter((template) => template.organizationId === orgId)).pipe(
      delay(LIST_LATENCY_MS),
    );
  }

  byId(id: string): QuestionnaireTemplate | undefined {
    return this.templates().find((template) => template.id === id);
  }

  /** Publishing bumps the version and clears drift on every account still linked to the template. */
  publish(id: string): Observable<QuestionnaireTemplate | undefined> {
    this.templates.update((list) =>
      list.map((template) =>
        template.id === id
          ? {
              ...template,
              status: 'published',
              version: template.version + 1,
              lastPublishedIso: new Date().toISOString(),
              usage: template.usage.map((entry) =>
                entry.detached ? entry : { ...entry, hasDrifted: false },
              ),
            }
          : template,
      ),
    );
    return of(this.byId(id)).pipe(delay(SAVE_LATENCY_MS));
  }

  detach(templateId: string, accountId: string): Observable<QuestionnaireTemplate | undefined> {
    this.templates.update((list) =>
      list.map((template) =>
        template.id === templateId
          ? {
              ...template,
              usage: template.usage.map((entry) =>
                entry.accountId === accountId
                  ? { ...entry, detached: true, hasDrifted: false }
                  : entry,
              ),
            }
          : template,
      ),
    );
    return of(this.byId(templateId)).pipe(delay(SAVE_LATENCY_MS));
  }

  retire(id: string): Observable<void> {
    this.templates.update((list) =>
      list.map((template) => (template.id === id ? { ...template, status: 'retired' } : template)),
    );
    return of(undefined).pipe(delay(SAVE_LATENCY_MS));
  }
}
